import React, { useState, useEffect, useRef } from 'react'
import { GlobalContext } from './GlobalContext'
import { CognitoIdentityProviderClient, InitiateAuthCommand } from "@aws-sdk/client-cognito-identity-provider"
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useParams,
  useNavigate,
  json,
  ScrollRestoration,
} from "react-router-dom"

// utilities
import CloudformationOutputs from './CloudformationOutputs.json'
import { checkIfSubscriptionExpiringWithoutPayment } from './utils/checkIfSubscriptionExpiringWithoutPayment'
import { publish } from './utils/pubsub'
// UI Elements
import Footer from './ui-elements/Footer'
import Header from './ui-elements/Header'
import HorizontalScroll from './ui-elements/HorizontalScroll'
// account setup
import AssignFridgesToGroups from './account-setup-flow/AssignFridgesToGroups'
import CheckTimes from './account-setup-flow/CheckTimes'
import Complete from './account-setup-flow/Complete'
import GetStarted from './account-setup-flow/GetStarted'
import Groups from './account-setup-flow/Groups'
import IntroBanner from './account-setup-flow/IntroBanner'
// assets app
import AssetMap from './asset-management/AssetMap'
import EditAsset from './asset-management/EditAsset'
import ListAssets from './asset-management/ListAssets'
import ServiceRecord from './asset-management/ServiceRecord'
// first aid app
import ListIncidents from './firstaid/ListIncidents'
import SingleIncident from './firstaid/SingleIncident'
// fridges
import FridgeProfiles from './fridges/FridgeProfiles'
import ListFridges from './fridges/ListFridges'
import ListGroups from './fridges/ListGroups'
import ListReadings from './fridges/ListReadings'
// shared components
import AppTermsDialog from './shared-components/AppTermsDialog'
import CardJustAdded from './shared-components/CardJustAdded'
import Dashboard from './dashboard/Dashboard'
import FeatureNotEnabled from './shared-components/FeatureNotEnabled'
import ListUsers from './shared-components/ListUsers'
import Payments from './shared-components/Payments'
import SplashPage from './shared-components/SplashPage'
import SubscriptionExpired from './shared-components/SubscriptionExpired'
import SubscriptionExpiring from './shared-components/SubscriptionExpiring'
import SuperAdmin from './shared-components/SuperAdmin'
import Notifications from './shared-components/Notifications'
import StickerPacks from './fridges/StickerPacks'
//Checklists
import ChecklistDetails from './checklists/ChecklistDetails'
import MyChecklists from './checklists/MyChecklists'
import ChecklistResultsDay from './checklists/ChecklistResultsDay'
import ChecklistResultsMonth from './checklists/ChecklistResultsMonth'
import ChecklistSetupWrapper from './checklists/wrapper/ChecklistSetupWrapper'
import SharepointConnector from './integrations/SharepointConnector'
import AccessDenied from './ui-elements/AccessDenied'
import PresetChecklists from './checklists/PresetChecklists'
import AddStickerPack from './fridges/AddStickerPack'
import AddEditFridge from './fridges/AddEditFridge'
import DailyChecklistSummary from './shared-components/DailyChecklistSummary'
import Resellers from './shared-components/Resellers'
import MessageModal from './ui-elements/MessageModal'
import AccountSettings from './account-settings/AccountSettings'
import RegexBuilder from './shared-components/RegexBuilder'
import CustomDataFormats from './checklists/CustomDataFormats'
import AssetTypeList from './asset-management/AssetTypeList'
import Spinner from './ui-elements/Spinner'
import AssetTypeEdit from './asset-management/AssetTypeEdit'
import ListProcesses from './processes/ListProcesses'
import AddEditProcess from './processes/AddEditProcess'
import Results from './results/Results'
import { updateProcessData } from './utils/updateProcessData'
import ChecklistQuestions from './checklists/ChecklistQuestions'
import ProcessReportingOptions from './processes/ProcessReportingOptions'
import ProcessReportingList from './processes/ProcessReportingList'
import NotLoggedIn from './auth/NotLoggedIn'
import NoOrganisation from './ui-elements/NoOrganisation'
import ManageChecklistCategories from './categories/ManageChecklistCategories'
import ChecklistHistory from './checklists/ChecklistHistory'
import ReconnectModal from './ui-elements/ReconnectModal'
import LoginModal from './ui-elements/LoginModal'
import ExportChecklistResults from './checklists/ExportChecklistResults'





type Organisations = null | ObjectStringKeyAnyValue

function App() {

  const [loggedIn, setLoggedIn] = useState<boolean | string>(false)
  const [idToken, setIdToken] = useState<string | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState<string | null>(null)
  const [socket, setSocket] = useState<WebSocket | null>(null)
  const [connectionState, setConnectionState] = useState('disconnected')
  const [orgSettings, setOrgSettings] = useState('disconnected')
  const [userData, setUserData] = useState<ObjectStringKeyAnyValue | null>(null)
  const [currentOrganisation, setCurrentOrganisation] = useState<string | null>(null)
  const [logoUrl, setLogoUrl] = useState<string>(`/logo-white-no-strapline.svg`)
  const WebSocketURI = CloudformationOutputs.WebSocketURI
  const [tableData, setTableData] = useState<ObjectStringKeyAnyValue | null>(null)
  const [loggedInUserRole, setLoggedInUserRole] = useState<string | null>(null)
  // const tableDataRef = useRef<any>(null)
  const [fridges, setFridges] = useState<ObjectStringKeyAnyValue | null>(null)
  const [currentStickerPack, setCurrentStickerPack] = useState<ObjectStringKeyAnyValue | null>(null)
  const [isAccountSetup, setIsAccountSetup] = useState<boolean | null>(null)
  const [activeFeatures, setActiveFeatures] = useState<{ [key: string]: any }>({})
  const [expiredFeatures, setExpiredFeatures] = useState<{ [key: string]: any }>({})
  const [agreementNeeded, setAgreementNeeded] = useState<{ [key: string]: any } | null>(null)
  const [subscriptionHasBeenChecked, setSubscriptionHasBeenChecked] = useState<boolean>(false)
  const [subscriptionExpired, setSubscriptionExpired] = useState<boolean>(false)
  const [subscriptionExpiringWithoutPayment, setSubscriptionExpiringWithoutPayment] = useState<boolean>(false)
  const [checklistSetupFields, setChecklistSetupFields] = useState<ObjectStringKeyAnyValue | null>(null)
  const [checklistQuestionFields, setChecklistQuestionFields] = useState<ObjectStringKeyAnyValue | null>(null)
  const [showModal, setShowModal] = useState<ObjectStringKeyAnyValue | null>(null)
  const [showReconnectModal, setShowReconnectModal] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [resultsData, setResultsData] = useState<ObjectStringKeyAnyValue | null>(null)
  const [processData, setProcessData] = useState<ObjectStringKeyAnyValue | null>(null)
  const [selectedResultsDate, setSelectedResultsDate] = useState<ObjectStringKeyAnyValue | null>(null)
  const [checklistsThatQuestionsHaveBeenRequestedFor, setChecklistsThatQuestionsHaveBeenRequestedFor] = useState<Array<String>>([])
  const [checklistsThatHistoryHasBeenRequestedFor, setChecklistsThatHistoryHasBeenRequestedFor] = useState<Array<String>>([])
  const [datesAndChecklistIdsCheckPeriodsRequestedFor, setDatesAndChecklistIdsCheckPeriodsRequestedFor] = useState<Array<String>>([])
  const [checklistHistories, setChecklistHistories] = useState<ObjectStringKeyAnyValue>({})
  const lastKnownProcessDefinitionValueRef = useRef<any>(null)
  const lastKnownProcessRecordsValueRef = useRef<any>(null)
  const lastKnownCheckPeriodsValueRef = useRef<any>(null)
  const socketRef = useRef<WebSocket | null>(null)
  //console.log(tableData)
  // tableData && tableData.Checklists && console.log(Object.keys(tableData.Checklists).length)


  useEffect(() => {
    // set the socket ref to the current socket
    if (socket) {
      socketRef.current = socket
    } else {
      socketRef.current = null
    }
  }, [socket])

  useEffect(() => {
    // tesitng functionalty to test websocket reconnection
    // listen for hotkey press ctrl + 1.  if pressed force socket to close 
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === '1') {
        console.log('🔌  forcing socket to close')
        if (socket) {
          socket.close()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [socket])

  useEffect(() => {
    checkForToken()
    setConnectionState('tryToConnect')

  }, [])

  useEffect(() => {
    if (currentStickerPack) {
      localStorage.setItem('currentStickerPack', JSON.stringify(currentStickerPack))
    } else {
      if (localStorage.getItem('currentStickerPack')) {
        setCurrentStickerPack(JSON.parse(`${localStorage.getItem('currentStickerPack')}`))
      }
    }
  }, [currentStickerPack])

  useEffect(() => {
    switch (connectionState) {
      case 'closed':
      case 'error':
        setShowReconnectModal(true)
        setConnectionState('waitThenReconnect')
        setTimeout(() => {
          setConnectionState('tryToConnect')
        }, 200)
        break;
      case 'tryToConnect':
        setShowReconnectModal(true)
        setConnectionState('connecting')
        if (!socket) {
          const newSocket = new WebSocket(WebSocketURI)
          console.log("🔌  opening websocket...")
          newSocket.onopen = () => {
            setSocket(newSocket)
            console.log("🔌  socket has been opened")
          }
          newSocket.onclose = () => {
            console.log("🔌  socket closed")
            setConnectionState('closed')
            setSocket(null) 
          }
          newSocket.onerror = () => {
            setConnectionState('error')
            console.log("🔌 socket error")
            setSocket(null) 
          }
        } else {
          console.log("🔌  socket already exists, not creating a new one")
        }
        break

      case 'connecting':
        if (socket){
            setConnectionState('connected-awaiting-login')
        }
        break

      case 'connected-awaiting-login':
        setShowReconnectModal(true)
        console.log("🔌  connected and awaiting login")
        console.log(socket ? 'socket exists' : 'socket does not exist')
        break

      case 'connected-and-logged-in':
        setShowReconnectModal(false)
        console.log("🔌  connected and logged in")
        break

    }

  }, [connectionState, socket])

  useEffect(() => {
    if (tableData) {

      checkIfSubscriptionExpiringWithoutPayment(tableData, subscriptionHasBeenChecked, setSubscriptionExpiringWithoutPayment, setSubscriptionHasBeenChecked)

      // set logged in user role
      if (tableData.Users && userData) {
        const loggedInUserId = userData['cognito:username']
        const role = tableData.Users[loggedInUserId] ? tableData.Users[loggedInUserId].Role : null
        setLoggedInUserRole(role)
      }
    }

  }, [tableData, userData])

  useEffect(() => {
    if (!tableData) {
      return
    }
    // see if changes have been made to tableData['Processes'] or tableData['ProcessRecords']
    // use refs to compare the previous value to the current value
    if (tableData && (tableData.ProcessDefinitions || tableData.ProcessRecords)) {
      if (lastKnownProcessDefinitionValueRef.current !== tableData.ProcessDefinitions || lastKnownProcessRecordsValueRef.current !== tableData.ProcessRecords || lastKnownCheckPeriodsValueRef.current !== tableData.ChecklistPeriods) {
        lastKnownProcessDefinitionValueRef.current = tableData.Processes
        lastKnownProcessRecordsValueRef.current = tableData.ProcessRecords
        lastKnownCheckPeriodsValueRef.current = tableData.ChecklistPeriods
        const newData = updateProcessData(tableData)
        setProcessData(newData)
      }
    } else {
      // console.log("no changes to process data")
    }
  }, [tableData, selectedResultsDate])


  useEffect(() => {
    // handle incoming messages
    if (socket !== null) {
      actOnMessage()
    }
  }, [socket])

  useEffect(() => {
    handleTokens()
  }, [idToken, accessToken, connectionState, loggedIn, currentOrganisation, refreshToken])


  const handleTokens = async () => {

    // immediately after logging in the idToken, accessToken and refreshToken will only be in localStorage! copy to state if not already there
    if(!idToken && localStorage.getItem('localIdToken')) {
      setIdToken(localStorage.getItem('localIdToken'))
    }
    if(!accessToken && localStorage.getItem('localAccessToken')) {
      setAccessToken(localStorage.getItem('localAccessToken'))
    }
    if(!refreshToken && localStorage.getItem('localRefreshToken')) {
      setRefreshToken(localStorage.getItem('localRefreshToken'))
    }

    if (idToken && accessToken && refreshToken && connectionState === 'connected-awaiting-login') {
      //console.log("Handling tokens", refreshToken)
      const tokensExpired = checkIfTokensExpired(accessToken, idToken, refreshToken)
      if (tokensExpired) {
        setLoggedIn(false)
        console.log("Tokens expired, refreshing...")
        const newTokens = await fetchRefreshedTokens(refreshToken)
        if (newTokens && newTokens.id_token && newTokens.access_token) {
          console.log("🎱 New tokens received")
          setIdToken(newTokens.id_token)
          setAccessToken(newTokens.access_token)
          localStorage.setItem('localIdToken', newTokens.id_token)
          localStorage.setItem('localAccessToken', newTokens.access_token)
          sendTokensToWebsocket(newTokens.access_token, newTokens.id_token, newTokens.refresh_token)
        } else {
          console.log("Error: Failed to refresh tokens")
          console.log(newTokens)
          handleLogout()
        }
      } else {
        //console.log("Tokens are still valid")
        sendTokensToWebsocket(accessToken, idToken, refreshToken)
      }
    }
  }


  const sendTokensToWebsocket = async (accessToken: string, idToken: string, refreshToken: string) => {
    //console.log("Sending tokens to websocket")
    const payloadObj: { [index: string]: string } = {
      action: "login",
      loggingInFromApp: "dashboard",
      idToken: idToken,
      accessToken: accessToken
    }
    const selectedOrgId = localStorage.getItem("selectedOrgId")
    if (selectedOrgId) {
      payloadObj['selectedOrgId'] = selectedOrgId
    }
    const payload = JSON.stringify(payloadObj)
    sendMessageToWebsocket(payload)
  }

  const checkIfTokensExpired = (accessToken: string, idToken: string, refreshToken: string) => {
    try {
      const tokenExpiry = new Date(JSON.parse(atob(idToken.split('.')[1])).exp * 1000)
      const currentTime = new Date()
      const timeUntilExpiry = tokenExpiry.getTime() - currentTime.getTime()
      const refreshIfRemainingTimeIsLessThan = 5 * 60 * 1000 // 5 minutes in milliseconds
      //console.log(`Time until expiry: ${timeUntilExpiry / 60000}mins. Time limit: ${refreshIfRemainingTimeIsLessThan / 60000}mins`)
      if (timeUntilExpiry < refreshIfRemainingTimeIsLessThan) {
        return true
      }
      else {
        return false
      }
    } catch (error) {
      console.error("Error decoding token:", error)
      return false
    }
  }


  const fetchRefreshedTokens = async (refreshToken: string) => {
    //console.log("Fetching refreshed tokens for token: ", refreshToken)
    const clientId = CloudformationOutputs.UserPoolClientId
    const userPoolDomain = CloudformationOutputs.UserPoolDomain
    const awsRegion = CloudformationOutputs.AWSRegion
    const url = `https://${userPoolDomain}.auth.${awsRegion}.amazoncognito.com/oauth2/token`

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: clientId,
      refresh_token: refreshToken
    })

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: body.toString()
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(`Error refreshing token: ${error.error_description}`)
      }

      const data = await response.json()
      //console.log('New tokens:', data)
      return data
    } catch (error) {
      console.error('Failed to refresh token:', error)
      return null
    }
  }











  const checkForToken = () => {
    //console.log('checking for a token')
    const localIdToken = localStorage.getItem("localIdToken")
    const localAccessToken = localStorage.getItem("localAccessToken")
    const localRefreshToken = localStorage.getItem("localRefreshToken")
    const queryStringParams = window.location.hash.replace("#", "").split(/[&=]+/)

    let idToken = null
    let accessToken = null
    let refreshToken = null

    const indexOfIdToken = queryStringParams.indexOf('id_token')
    if (indexOfIdToken >= 0) {
      //console.log(`id token found at index ${indexOfIdToken}`)
      idToken = queryStringParams[indexOfIdToken + 1]
      //console.log(`🎱 id token found: ${idToken}`)
    } else {
      //console.log(`id token not found in query string`)
    }

    const indexOfAccessToken = queryStringParams.indexOf('access_token')
    if (indexOfAccessToken >= 0) {
      //console.log(`access token found at index ${indexOfAccessToken}`)
      accessToken = queryStringParams[indexOfAccessToken + 1]
      //console.log(`🥎 access token found: ${accessToken}`)
    }


    const indexOfRefreshToken = queryStringParams.indexOf('refresh_token')
    if (indexOfRefreshToken >= 0) {
      refreshToken = queryStringParams[indexOfRefreshToken + 1]
      //console.log(`🏀 refresh token found: ${refreshToken}`)
    }


    if (idToken && accessToken && refreshToken) {
      console.log('login pending')
      setLoggedIn('pending')
      setIdToken(idToken)
      setAccessToken(accessToken)
      setRefreshToken(refreshToken)
      localStorage.setItem('localIdToken', idToken)
      localStorage.setItem('localAccessToken', accessToken)
      localStorage.setItem('localRefreshToken', refreshToken)
      window.location.href = '/'

    } else if (localIdToken && localAccessToken && localRefreshToken) {
      //console.log('logged in earlier')
      setIdToken(localIdToken)
      setAccessToken(localAccessToken)
      setRefreshToken(localRefreshToken)
    } else {
      //console.log('not logged in')
      //window.location.href = loginUrl
    }
  }

  const switchCurrentOrganisation = (organisationId: string) => {
    //console.log(`Organization id change requested to ${organisationId}`)
    localStorage.setItem("selectedOrgId", organisationId)
    setCurrentOrganisation(organisationId)
    setTableData(null)
    socket?.close()
    window.location.href = '/'
  }

  const sendMessageToWebsocket = async (payload: string | object) => {
    if (socketRef.current) {
      if (typeof (payload) != 'string') {
        payload = JSON.stringify(payload)
      }
      //console.log(`🥝 sending to websocket: ${JSON.parse(payload).action}`)
      socketRef.current.send(payload)
    } else {
      console.log('🚫  Can not send to web socket')
    }
  }

  function checkPaymentDetailsAndRedirect(redirectUrl: string) {
    const lastPaymentTimestamp = localStorage.getItem('lastPaymentRequestTimestamp');
    const currentTime = Date.now();

    // Check if the timestamp exists and is less than 2 minutes ago
    if (lastPaymentTimestamp) {
      const timeElapsed = currentTime - parseInt(lastPaymentTimestamp, 10);

      // If less than 10 minutes have passed, do nothing
      if (timeElapsed < 2 * 60 * 1000) {
        console.log('Payment details were requested less than 2 minutes ago. No redirect.');
        return;
      }
    }
    // If the timestamp doesn't exist or more than 2 minutes have passed, update the timestamp and redirect
    window.location.href = redirectUrl;
  }



  function mergeObjects(oldData: any, newData: any): any {
    if (Array.isArray(oldData) || Array.isArray(newData)) {
      return newData; // Preserve arrays by replacing old with new
    }

    if (typeof oldData !== 'object' || oldData === null) return newData;
    if (typeof newData !== 'object' || newData === null) return oldData;

    let result = { ...oldData };

    for (let key in newData) {
      if (newData.hasOwnProperty(key)) {
        result[key] = mergeObjects(oldData[key], newData[key]);
      }
    }

    return result;
  }

  const mergeTableData = (oldTableData: ObjectStringKeyAnyValue, newData: ObjectStringKeyAnyValue) => {
    let mergedTableData: ObjectStringKeyAnyValue = {}
    if (oldTableData) {
      mergedTableData = { ...oldTableData };
      Object.keys(newData).forEach(key => {
        // Directly assigning the value from newData, overriding the oldData value if key exists in both
        mergedTableData[key] = newData[key];
      });
    } else {
      mergedTableData = { ...newData }
    }
    return mergedTableData
  }

  const setSingleItem = (oldTableData: ObjectStringKeyAnyValue, payload: ObjectStringKeyAnyValue) => {
    const resultTypeKey = Object.keys(payload)[0]
    const result = payload[resultTypeKey]
    const resultKey = Object.keys(result)[0]
    const resultValue = result[resultKey]
    let newTableDataObj: ObjectStringKeyAnyValue = {}
    if (oldTableData) {
      newTableDataObj = { ...oldTableData }
    }
    newTableDataObj[resultTypeKey] ||= {}
    newTableDataObj[resultTypeKey][resultKey] = resultValue
    return newTableDataObj
  }

  const setSingleItemFromNewRecord = (oldTableData: ObjectStringKeyAnyValue, payload: ObjectStringKeyAnyValue) => {
    const tableToUpdate = payload['tableName']
    const itemToUpdate = payload['keys'] ? payload['keys'][Object.keys(payload['keys'])[0]] : null
    const newValues = payload['newImage'] || payload['newRecord']
    //console.log(`Setting single item from new record ${tableToUpdate} ${itemToUpdate} ${JSON.stringify(Object.keys(newValues))}`)
    let newTableDataObj: ObjectStringKeyAnyValue = {}
    if (oldTableData) {
      newTableDataObj = { ...oldTableData }
    }
    newTableDataObj[tableToUpdate] ||= {}

    if (itemToUpdate) {
      newTableDataObj[tableToUpdate][itemToUpdate] = newValues
    } else {
      newTableDataObj[tableToUpdate] = newValues
    }
    return newTableDataObj
  }

  const setSingleTableData = (oldTableData: ObjectStringKeyAnyValue, payload: ObjectStringKeyAnyValue) => {
    const resultTypeKey = Object.keys(payload)[0]
    const result = payload[resultTypeKey]
    let newTableDataObj: ObjectStringKeyAnyValue = {}
    if (oldTableData) {
      newTableDataObj = { ...oldTableData }
    }
    newTableDataObj[resultTypeKey] ||= {}
    newTableDataObj[resultTypeKey] = result
    return newTableDataObj
  }

  const removeSingleItem = (oldTableData: ObjectStringKeyAnyValue, payload: ObjectStringKeyAnyValue) => {
    const tableNameToUpdate = payload['tableName']
    const itemToRemove = payload['keys'][Object.keys(payload['keys'])[0]]
    let newTableDataObj: ObjectStringKeyAnyValue = {}
    if (oldTableData) {
      newTableDataObj = { ...oldTableData }
    }
    newTableDataObj[tableNameToUpdate] ||= {}
    if (newTableDataObj[tableNameToUpdate] && newTableDataObj[tableNameToUpdate][itemToRemove]) {
      delete newTableDataObj[tableNameToUpdate][itemToRemove]
    }

    return newTableDataObj
  }


  const mergeSingleTableData = (oldTableData: ObjectStringKeyAnyValue, payload: ObjectStringKeyAnyValue) => {
    const resultTypeKey = Object.keys(payload)[0]
    const result = payload[resultTypeKey]
    let newTableDataObj: ObjectStringKeyAnyValue = {}
    if (oldTableData) {
      newTableDataObj = { ...oldTableData }
    }
    newTableDataObj[resultTypeKey] ||= {}
    for (const key in result) {
      newTableDataObj[resultTypeKey][key] = result[key]
    }
    return newTableDataObj
  }

  const updateUserTable = (oldTableData: ObjectStringKeyAnyValue, payload: ObjectStringKeyAnyValue) => {
    //console.log(`Updating user table ${JSON.stringify(payload)}`)
    const tableNameToUpdate = 'Users'
    const newRecords = payload
    let newTableDataObj: ObjectStringKeyAnyValue = {}
    if (oldTableData) {
      newTableDataObj = { ...oldTableData }
    }
    newTableDataObj[tableNameToUpdate] = newRecords
    return newTableDataObj
  }

  const actOnMessage = () => {
    if (socket && socket.onmessage === null) {
      socket.onmessage = ({ data }: any) => {
        if (data) {
          const action = JSON.parse(data).action
          const payload = JSON.parse(data).payload
          publish(action, payload)
          //console.log(`📨 A message has been received: ${action }`)
          switch (action) {
            case 'error':
              console.log(`🚫  Error: ${JSON.stringify(payload)}`)
              if (payload && payload.message) {
                setShowModal({ "error": ` ${payload.message}` })
              }
              break

            case 'autherror':
              console.log('🚫  Authentication error - logging you out')
              handleLogout()
              break

            case 'loggedIn':
              //console.log("🪵 logged in ")
              const loginDataBody = JSON.parse(data).body
              //console.log(loginDataBody)
              setConnectionState('connected-and-logged-in')
              //setShowModal(null)
              setShowLoginModal(false)
              setLoggedIn(true)
              setUserData(loginDataBody.idTokenDecoded.payload)
              setOrgSettings(loginDataBody.settings)

              setActiveFeatures(loginDataBody['activeFeatures'] || {})
              setExpiredFeatures(loginDataBody['expiredFeatures'] || {})

              const loggedInOrgId = loginDataBody.organisationId
              if (loggedInOrgId) {
                setCurrentOrganisation(loggedInOrgId)
                localStorage.setItem('selectedOrgId', loggedInOrgId)
              }
              let requestAssets = JSON.stringify({
                action: "fetchMultiTableData",
                tablesToFetch: ['Organisation', 'Assets', 'AssetTypes', 'Groups', 'Checklists', 'Users', 'CustomDataFormats', 'ProcessDefinitions', 'ChecklistCategories'],
              })
              sendMessageToWebsocket(requestAssets)
              break


            case 'loginFailed':
              handleLogout()
              break

            case 'loginOKButNoOrganisation':
              console.log("🚫  Login OK but no organisation")
              window.localStorage.setItem('loggedInNoOrgId', data)
              if (JSON.parse(data) && JSON.parse(data).idTokenDecoded) {
                setUserData(JSON.parse(data).idTokenDecoded.payload)
              }
              setLoggedIn(true)
              break
            case 'returnMultiTableData':
              //console.log(`❎ Multiple tables received`)
              //console.log(`❎ Multiple tables received: ${JSON.stringify(Object.keys(payload))}`)
              console.log("🍌 1")
              setTableData((oldTableData: ObjectStringKeyAnyValue) => mergeTableData(oldTableData, payload))
              break

            case 'returnSingleItem':
              console.log("🍌 2")
              setTableData((oldTableData: ObjectStringKeyAnyValue) => setSingleItem(oldTableData, payload))
              break

            case 'mergeSingleTableData':
              //console.log("🍌 3")
              setTableData((oldTableData: ObjectStringKeyAnyValue) => mergeSingleTableData(oldTableData, payload))
              break

            case 'returnSingleTableData':
              //console.log("🍌 4")
              setTableData((oldTableData: ObjectStringKeyAnyValue) => setSingleTableData(oldTableData, payload))
              break

            case 'tableUpdateItemAdded':
              //  console.log("🍌 5")
              setTableData((oldTableData: ObjectStringKeyAnyValue) => setSingleItemFromNewRecord(oldTableData, payload))
              break
            case 'tableUpdateItemModified':
              //console.log("🍌 6")
              setTableData((oldTableData: ObjectStringKeyAnyValue) => setSingleItemFromNewRecord(oldTableData, payload))
              break
            case 'tableUpdateItemRemoved':
              //console.log("🍌 7")
              setTableData((oldTableData: ObjectStringKeyAnyValue) => removeSingleItem(oldTableData, payload))
              break
            case 'ok':
              //console.log('super admin command ok')
              break

            case 'agreementNeeded':
              console.log("📝 Electronic agreement needed")
              console.log(payload)
              setAgreementNeeded(payload)
              break;
            case 'agreementSaved':
              console.log("📝 Electronic agreement saved")
              console.log(payload)
              setAgreementNeeded(null)
              break;
            case 'whiteLabelDetails':
              if (payload.logoUrl) {
                setLogoUrl(payload.logoUrl)
              }
              break
            case 'addUserResponse':
            case 'deleteUserResponse':
            case 'switchUserRoleResponse':
            case 'updateUserResponse':
              setTableData((oldTableData: ObjectStringKeyAnyValue) => updateUserTable(oldTableData, payload))
              break
            case 'paymentMethodNeeded':
              checkPaymentDetailsAndRedirect(payload['portalUrl'])
              break;

            case 'returnChecklistHistory':
              const checklistId = payload['checklistId']
              const history = payload['history']
              setChecklistHistories((oldChecklistHistory: ObjectStringKeyAnyValue) => {
                const newChecklistHistoriesObj: ObjectStringKeyAnyValue = { ...oldChecklistHistory }
                const oldHistory = newChecklistHistoriesObj[checklistId] || {}
                const newHistory = { ...oldHistory, ...history }
                newChecklistHistoriesObj[checklistId] = newHistory
                return newChecklistHistoriesObj
              })
              break

            case 'returnQuestionHistory':
              const questionChecklistId = payload['checklistId']
              const questionHistory = payload['history']
              setChecklistHistories((oldChecklistHistory: ObjectStringKeyAnyValue) => {
                const newChecklistHistoriesObj: ObjectStringKeyAnyValue = { ...oldChecklistHistory }
                const oldHistory = newChecklistHistoriesObj[questionChecklistId] || {}
                const newHistory = { ...oldHistory, ...questionHistory }
                newChecklistHistoriesObj[questionChecklistId] = newHistory
                return newChecklistHistoriesObj
              })
              break

            case 'incidentRiddorResponse':
            case 'timeZoneListResponse':
            case 'addItemSignedUploadURL':
            case 'updateItemSignedUploadURL':
            case 'addItemResponse':
            case 'updateItemResponse':
            case 'deleteItemResponse':
            case "documentReadyForDownloadResponse":
            case 'assignStickerPackIdToOrgResponse':
            case 'assignFridgesToGroupsResponse':
            case 'makeStickerPackResponse':
            case 'generateFridgeGroupsResponse':
            case 'returnCheckingTimes':
            case 'returnFridgeGroups':
            case 'notificationPreferencesResponse':
            case 'loginPreferencesResponse':
            case 'makeAuditSheetResponse':
            case 'checklistUpdatedResponse':
            case 'checklistQuestionUpdatedResponse':
            case 'checklistQuestionDeletedResponse':
            case 'qrCodeGenerated':
            case 'checklistDeletedResponse':
            case 'changeQuestionOrderResponse':
            case 'returnSharepointLoginUrl':
            case 'returnSharepointColumnsForList':
            case 'returnSharepointSites':
            case 'returnSharepointMappings':
            case 'returnSharepointConnectionCheckResult':
            case 'returnSharepointListsForSite':
            case 'returnSharepointMappingSaved':
            case 'updateChecklistOptionsResponse':
            case 'presetChecklistAdded':
            case 'returnDailyChecklistSummary':
            case 'cloneOrgResponse':
            case 'returnPresignedUrl':
            case 'resellerAdded':
            case 'deletedReseller':
              // handled when pub sub or when table data is updated
              break

            default:
              console.log(`⛔️ A message has been received but the action (${action}) is invalid`)
              //console.log(`A message has been received but the action is invalid. ${data}`)
              break
          }
        }
      }
    }
  }

  const handleLogout = () => {
    setLoggedIn(false)
    setUserData(null)
    setAccessToken(null)
    setIdToken(null)
    setRefreshToken(null)
    localStorage.removeItem('localIdToken')
    localStorage.removeItem('localAccessToken')
    localStorage.removeItem('localRefreshToken')
    // close the websocket
    if (socket) {
      console.log("🔌  Closing websocket because logout")
      if(socket.readyState === WebSocket.OPEN) {
      socket.close()
        setConnectionState('disconnected')
      } else {
        console.log("🔌  Websocket is not open, not closing")
      }
    }
  }

  const getHistoryForChecklist = (checklistId: string) => {
    //console.log(`Getting history for checklist: ${checklistId}`)
    if (!checklistsThatHistoryHasBeenRequestedFor.includes(checklistId)) {
      //console.log(`🔎 requesting history for checklist: ${checklistId}`)
      setChecklistsThatHistoryHasBeenRequestedFor(prev => [...prev, checklistId])
      const payload = JSON.stringify({
        action: 'checklists',
        subAction: 'getHistoryForChecklist',
        checklistId: checklistId
      })
      sendMessageToWebsocket(payload)
    } else {
      console.log(`🔎 history already requested for: ${checklistId}`)
    }
  }



  const getQuestionsForChecklist = (checklistId: string) => {
    if (!checklistsThatQuestionsHaveBeenRequestedFor.includes(checklistId)) {
      //console.log(`🔎 Getting questions for checklist: ${checklistId}`)
      setChecklistsThatQuestionsHaveBeenRequestedFor([...checklistsThatQuestionsHaveBeenRequestedFor, checklistId])
      const payload = JSON.stringify({
        action: 'checklists',
        subAction: 'getQuestionsForChecklist',
        checklistId: checklistId
      })
      sendMessageToWebsocket(payload)
    } else {
      //console.log(`🔎 Questions already requested for: ${checklistId}`)
    }
  }

  const getCheckPeriodsForChecklistAndTimeRange = (checklistId: string, year: string, month: string, day: string | null) => {
    const checklistIdAndDate = `${checklistId}-${year}-${month}${day ? `-${day}` : ''}`
    getQuestionsForChecklist(checklistId)
    if (!datesAndChecklistIdsCheckPeriodsRequestedFor.includes(checklistIdAndDate)) {
      //console.log(`🔎 Getting check periods for checklist: ${checklistId} for date: ${year}-${month}${day ? `-${day}` : ''}`)
      setDatesAndChecklistIdsCheckPeriodsRequestedFor([...datesAndChecklistIdsCheckPeriodsRequestedFor, checklistIdAndDate])
      const payload = JSON.stringify({
        action: 'checklists',
        subAction: 'fetchChecklistInfoByTimePeriod',
        checklistId: checklistId,
        year: year,
        month: month,
        day: day,
        includeAnswers: true
      })
      sendMessageToWebsocket(payload)
    } else {
      //console.log(`🔎 check periods already requested for: ${checklistIdAndDate}`)
    }
  }



  if (!loggedIn) {
    return <GlobalContext.Provider value={{
      tableData,
      userData,
      currentOrganisation,
      switchCurrentOrganisation,
      sendMessageToWebsocket,
      activeFeatures,
      expiredFeatures,
      loggedIn,
      orgSettings,
      setShowModal,
      accessToken,
      idToken,
      connectionState,
      showLoginModal,
      setShowLoginModal,
      socket
    }}><Router>
        {showLoginModal && <LoginModal />}
        {showModal && <MessageModal showModal={showModal} />}
        <Routes>
          <Route path='/*' element={<NotLoggedIn showForm={'login'} />} />
          <Route path='/forgotPassword' element={<NotLoggedIn showForm={'forgotPassword'} />} />
        </Routes>
      </Router>
    </GlobalContext.Provider>
  }



  if (loggedIn && loggedInUserRole === 'user') {
    return <div className={`flex flex-col min-h-screen justify-between`}>
      <div className={`flex flex-col flex-grow`}>
        <Router>
          <Header handleLogout={handleLogout} logoUrl={logoUrl} />
          <div className={`flex flex-col items-center`}>
            <div className={`max-w-3xl w-full p-5 flex flex-col gap-5 `}>
              <AccessDenied />
            </div>
          </div>
        </Router>
      </div>
      <Footer
      />
    </div>
  }







  return <div className={`h-full flex flex-col min-h-screen justify-between`}>




    <div className={`h-full bg-gray-200 flex flex-col flex-grow`}>
      <GlobalContext.Provider value={{
        tableData,
        userData,
        fridges,
        currentOrganisation,
        switchCurrentOrganisation,
        sendMessageToWebsocket,
        checklistSetupFields,
        setChecklistSetupFields,
        checklistQuestionFields,
        setChecklistQuestionFields,
        activeFeatures,
        expiredFeatures,
        loggedIn,
        orgSettings,
        setShowModal,
        processData,
        selectedResultsDate,
        setSelectedResultsDate,
        getQuestionsForChecklist,
        getHistoryForChecklist,
        checklistHistories,
        getCheckPeriodsForChecklistAndTimeRange,
        connectionState
      }}>
        {subscriptionExpired && <SubscriptionExpired />}
        {subscriptionExpiringWithoutPayment && <SubscriptionExpiring
          setSubscriptionExpiringWithoutPayment={setSubscriptionExpiringWithoutPayment}
        />}
        <Router>

          {showReconnectModal && <ReconnectModal />}
          {showModal && <MessageModal showModal={showModal} />}


          <Header handleLogout={handleLogout} logoUrl={logoUrl} />


          {agreementNeeded && <AppTermsDialog details={agreementNeeded} sendMessageToWebsocket={sendMessageToWebsocket} />}

          {!currentOrganisation && <NoOrganisation handleLogout={handleLogout} />}

          {!tableData && currentOrganisation && <div className={`mt-6`}><Spinner size='large'>Loading...</Spinner></div>}

          {currentOrganisation && tableData && <Routes>


            <Route path='/*' element={<Dashboard />} />
            <Route path='/sharepoint' element={<SharepointConnector />} />
            <Route path='/hs' element={<HorizontalScroll />} />

            <Route
              path='/listreadings'
              element={
                activeFeatures.hasOwnProperty('Fridges') ?
                  <ListReadings
                    setTableData={setTableData}
                  /> :
                  <AccessDenied />
              } />

            <Route
              path='/listreadings/:groupId'
              element={
                activeFeatures.hasOwnProperty('Fridges') ?
                  <ListReadings
                    setTableData={setTableData}
                  /> :
                  <AccessDenied />
              } />

            <Route path='/users' element={<ListUsers loggedInUserRole={loggedInUserRole} />} />

            <Route path='/account-settings' element={<AccountSettings />} />


            <Route
              path='/assets'
              element={<ListAssets />} />


            <Route
              path='/map'
              element={<AssetMap />} />

            <Route
              path='/asset'
              element={<EditAsset />} />

            <Route
              path='/asset/:Id'
              element={<EditAsset />} />

            <Route
              path='/asset-types'
              element={<AssetTypeList />} />

            <Route
              path='/asset-type'
              element={<AssetTypeEdit />} />

            <Route
              path='/asset-type/:Id'
              element={<AssetTypeEdit />} />


            <Route
              path='/notifications'
              element={
                activeFeatures.hasOwnProperty('Fridges') ?
                  <Notifications /> :
                  <AccessDenied />}
            />

            <Route
              path='/fridges'
              element={
                activeFeatures.hasOwnProperty('Fridges') ?
                  <ListFridges /> :
                  <AccessDenied />
              } />

            <Route
              path='/fridges/add-edit/'
              element={
                activeFeatures.hasOwnProperty('Fridges') ?
                  <AddEditFridge /> :
                  <AccessDenied />
              } />

            <Route
              path='/fridges/add-edit/:fridgeId'
              element={
                activeFeatures.hasOwnProperty('Fridges') ?
                  <AddEditFridge /> :
                  <AccessDenied />
              } />

            <Route
              path='/incidents'
              element={<ListIncidents />} />

            <Route
              path='/incident/:Id'
              element={<SingleIncident />} />

            <Route
              path='/servicerecord/:Id'
              element={<ServiceRecord />} />

            <Route
              path='/payments'
              element={<Payments />} />

            <Route
              path='/superadmin'
              element={<SuperAdmin handleLogout={handleLogout} />} />

            <Route
              path='/resellers'
              element={<Resellers />} />

            <Route
              path='/stickerpacks'
              element={<StickerPacks />} />

            <Route
              path='/card_added'
              element={<CardJustAdded />} />

            <Route
              path='checklists'
              element={<MyChecklists />} />

            <Route
              path='add-sticker-pack'
              element={<AddStickerPack />} />

            <Route
              path='checklists/presets'
              element={<PresetChecklists />} />
            <Route
              path='checklists/:checklistId'
              element={<ChecklistDetails />} />

            <Route
              path='/manage-checklist-categories'
              element={<ManageChecklistCategories />} />

            <Route
              path='/daily-checklist-summary/'
              element={<DailyChecklistSummary />} />
            <Route
              path='/daily-checklist-summary/:year/:month'
              element={<DailyChecklistSummary />} />
            <Route
              path='checklists/:checklistId/export-results/'
              element={<ExportChecklistResults />} />
            <Route
              path='checklists/:checklistId/results/'
              element={<ChecklistResultsMonth />} />
            <Route
              path='checklists/:checklistId/results/:year/:month'
              element={<ChecklistResultsMonth />} />
            <Route
              path='checklists/:checklistId/results/:year/:month/:day'
              element={<ChecklistResultsDay />} />
            <Route
              path='checklists/:checklistId/settings/'
              element={<ChecklistSetupWrapper showForm='settings' />} />

            <Route
              path='checklists/:checklistId/history/'
              element={<ChecklistHistory />} />


            <Route
              path='checklists/:checklistId/questions/'
              element={<ChecklistQuestions />} />

            <Route
              path='checklists/:checklistId/custom-template-fields/'
              element={<ChecklistSetupWrapper showForm='Custom template fields' />} />

            <Route
              path='checklists/:checklistId/questions/:questionId/'
              element={<ChecklistSetupWrapper showForm='questions' />} />

            <Route
              path='checklists/:checklistId/questions/:questionId/:parentQuestionId/:parentActionId'
              element={<ChecklistSetupWrapper showForm='questions' />} />

            <Route path='results/:resultType/' element={<Results />} />
            <Route path='results/:resultType/:processDefinitionId' element={<Results />} />
            <Route path='results/:resultType/:processDefinitionId/:year/:month/:day' element={<Results />} />


            <Route
              path='/processes'
              element={
                activeFeatures.hasOwnProperty('Processes') ?
                  <ListProcesses /> :
                  <AccessDenied />
              } />


            <Route
              path='/process'
              element={
                activeFeatures.hasOwnProperty('Processes') ?
                  <AddEditProcess /> :
                  <AccessDenied />
              } />

            <Route
              path='/process/:processDefinitionId'
              element={
                activeFeatures.hasOwnProperty('Processes') ?
                  <AddEditProcess /> :
                  <AccessDenied />
              } />

            <Route
              path='/process/:processDefinitionId/reporting'
              element={
                activeFeatures.hasOwnProperty('Processes') ?
                  <ProcessReportingList /> :
                  <AccessDenied />
              } />

            <Route
              path='/process/:processDefinitionId/reporting/:reportingOptionId'
              element={
                activeFeatures.hasOwnProperty('Processes') ?
                  <ProcessReportingOptions /> :
                  <AccessDenied />
              } />



            <Route path='/custom-data-formats'
              element={<CustomDataFormats />} />

            <Route path='/custom-data-builder'
              element={<RegexBuilder />} />

            <Route path='/custom-data-builder/:regexId'
              element={<RegexBuilder />} />


          </Routes>}
        </Router>
      </GlobalContext.Provider>

    </div>

    <Footer
    />
  </div>

}

export default App
